import { buildAccountOptions } from '@/components/cashflow/helpers';
import ApplyToCheckbox from '@/components/cashflow/rules/modal/ApplyToCheckbox';
import SubmitButton from '@/components/cashflow/rules/modal/SubmitButton';
import useCategoryOptions from '@/components/cashflow/useCategoryOptions';
import useMerchantSelect from '@/components/cashflow/useMerchantSelect';
import useTagOptions from '@/components/cashflow/useTagOptions';
import { StringOption } from '@/components/common/FancySelectInput';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { Eye } from '@/components/common/icons';
import InputField from '@/components/common/InputField';
import { BigHeader, LinkText, Text } from '@/components/common/Typography';
import YesNoSelect from '@/components/common/YesNoSelect';
import { Box, Collapse } from '@chakra-ui/react';
import { CategoryChange, MerchantChange, TagChange } from '@common/cashflow';
import {
    AMOUNT_TYPE_OPTIONS,
    CHANGE_CONFIGS,
    ChangeConfig,
    CONDITION_CONFIGS,
    ConditionConfig,
    Enabled,
    RuleChangeWithEnabled,
    RuleConditionWithEnabled,
    useRuleContext,
} from '@common/react/cashflow/rules';
import { useCurrentUser } from '@common/react/user';
import React, { useMemo } from 'react';

const RuleForm = () => {
    const {
        conditions,
        changes,
        handleConditionChange,
        handleChangeChange,
        rule,
        matchingTransactions,
        matchingTransactionsLoading,
        setView,
        isGlobalRule,
    } = useRuleContext();

    return (
        <FlexRow gap={2}>
            <FlexCol
                flex={1}
                borderRight="1px solid"
                borderColor="gray.200"
                pr={7}
                pt={12}
                pb={10}
                mt={-12}
                mb={-10}
            >
                <BigHeader mb={4}>
                    {rule
                        ? `Edit ${isGlobalRule ? 'global ' : ''}rule`
                        : `Create a ${isGlobalRule ? 'global ' : ''}rule`}
                </BigHeader>
                <Text color="gray.500" fontSize="sm" mb={8}>
                    The rules you set will apply to all transactions from now
                    on, and you can choose to apply to older transactions.
                </Text>
                <Text
                    color="blue.500"
                    fontSize="lg"
                    fontWeight="semibold"
                    mb={4}
                >
                    If transaction...
                </Text>
                <FlexCol gap={3} flexGrow={1}>
                    {CONDITION_CONFIGS.map((config) => {
                        const condition = conditions.find(
                            (c) => c.field === config.field
                        );

                        if (!condition) {
                            console.error(
                                'condition not found',
                                config.field,
                                conditions
                            );
                            return;
                        }

                        return (
                            <ConditionBlock
                                key={config.field}
                                conditionConfig={config}
                                condition={condition}
                                onChange={handleConditionChange}
                            />
                        );
                    })}
                </FlexCol>
                <FlexRow align="center" h={50} mt={10}>
                    {!matchingTransactionsLoading &&
                        matchingTransactions &&
                        matchingTransactions.length > 0 && (
                            <LinkText
                                onClick={() => {
                                    setView('transactions');
                                }}
                            >
                                <Eye display="inline" mr={2} />
                                Preview changes
                            </LinkText>
                        )}
                </FlexRow>
            </FlexCol>

            <FlexCol flex={1} pl={4} mt={-5}>
                <Text
                    color="green.600"
                    fontSize="lg"
                    fontWeight="semibold"
                    mt="130px"
                    mb={4}
                >
                    Apply these changes...
                </Text>
                <FlexCol gap={3} flexGrow={1}>
                    {CHANGE_CONFIGS.map((config) => {
                        const change = changes.find(
                            (c) => c.field === config.field
                        );

                        if (!change) {
                            console.error(
                                'change not found',
                                config.field,
                                changes
                            );
                            return;
                        }

                        return (
                            <ChangeBlock
                                key={config.field}
                                changeConfig={config}
                                change={change}
                                onChange={handleChangeChange}
                            />
                        );
                    })}
                </FlexCol>

                <FlexRow justify="space-between" align="center" h={50} mt={7}>
                    <ApplyToCheckbox />
                    <SubmitButton />
                </FlexRow>
            </FlexCol>
        </FlexRow>
    );
};

export default RuleForm;

const ConditionBlock = ({
    conditionConfig,
    condition,
    onChange,
}: {
    conditionConfig: ConditionConfig;
    condition: RuleConditionWithEnabled;
    onChange: (condition: RuleConditionWithEnabled) => void;
}) => (
    <Block
        enabled={condition.enabled}
        onToggle={(enabled) => onChange({ ...condition, enabled })}
        borderColor={condition.enabled ? 'blue.500' : 'gray.200'}
        label={conditionConfig.label}
        switchColorScheme="blue"
        cyLabel="condition-block"
    >
        <FlexRow justify="space-between" gap={2} align="center" w="full">
            {condition.field === 'amount' && (
                <InputField
                    name="amountType"
                    type="fancySelect"
                    onChange={(option) =>
                        onChange({ ...condition, amountType: option.value })
                    }
                    options={AMOUNT_TYPE_OPTIONS}
                    fancySelectOptions={{ isClearable: false }}
                    inputProps={{ size: 'sm' }}
                    value={condition.amountType}
                />
            )}

            {['merchantName', 'statement', 'amount'].includes(
                conditionConfig.field
            ) && (
                <InputField
                    name={`${conditionConfig.field}-matcher`}
                    type="fancySelect"
                    onChange={(option) =>
                        onChange({ ...condition, matcher: option.value })
                    }
                    options={conditionConfig.options}
                    fancySelectOptions={{ isClearable: false }}
                    inputProps={{ size: 'sm' }}
                    value={condition.matcher}
                />
            )}
        </FlexRow>

        <ConditionValueInput condition={condition} onChange={onChange} />
    </Block>
);

const ConditionValueInput = ({
    condition,
    onChange,
}: {
    condition: RuleConditionWithEnabled;
    onChange: (condition: RuleConditionWithEnabled) => void;
}) => {
    const categoryOptions = useCategoryOptions();
    const sharedProps = {
        inputProps: {
            variant: 'outline',
            size: 'sm',
        },
        name: condition.field,
    };
    const { partnerInfo } = useCurrentUser();
    const { financialEntities } = useRuleContext();
    const accountOptions = useMemo(
        () => buildAccountOptions(financialEntities, partnerInfo),
        [financialEntities, partnerInfo]
    );

    if (condition.field === 'merchantName' || condition.field === 'statement') {
        return (
            <InputField
                {...sharedProps}
                type="text"
                onChange={(value) => onChange({ ...condition, value })}
                value={condition.value}
            />
        );
    }

    if (
        condition.field === 'amount' &&
        ['equalTo', 'lessThan', 'greaterThan'].includes(condition.matcher)
    ) {
        return (
            <InputField
                {...sharedProps}
                type="currency"
                onChange={(value) => onChange({ ...condition, value })}
                inputOptions={{ min: 0 }}
                value={
                    Array.isArray(condition.value)
                        ? condition.value[0]
                        : condition.value
                }
            />
        );
    }

    if (condition.field === 'amount' && condition.matcher === 'between') {
        return (
            <FlexRow gap={2} align="center">
                <InputField
                    {...sharedProps}
                    name={`${condition.field}-value1`}
                    type="currency"
                    onChange={(value) => {
                        const val2 = Array.isArray(condition.value)
                            ? condition.value[1]
                            : 0;
                        onChange({ ...condition, value: [value, val2] });
                    }}
                    inputOptions={{ min: 0 }}
                    value={condition.value && condition.value[0]}
                />
                <Text fontSize="sm">and</Text>
                <InputField
                    {...sharedProps}
                    name={`${condition.field}-value2`}
                    type="currency"
                    onChange={(value) => {
                        const val1 = Array.isArray(condition.value)
                            ? condition.value[0]
                            : 0;
                        onChange({ ...condition, value: [val1, value] });
                    }}
                    inputOptions={{ min: 0 }}
                    value={condition.value && condition.value[1]}
                />
            </FlexRow>
        );
    }

    if (condition.field === 'categoryId') {
        return (
            <InputField
                {...sharedProps}
                type="fancySelect"
                fancySelectOptions={{
                    isClearable: false,
                    fullScreenPlacement: 'right',
                    menuWidth: 300,
                    placeholder: 'Select a subcategory...',
                }}
                onChange={(option: StringOption) => {
                    onChange({ ...condition, value: option.value });
                }}
                value={condition.value}
                options={categoryOptions}
                name="category"
            />
        );
    }

    if (condition.field === 'financialEntityId') {
        return (
            <InputField
                {...sharedProps}
                type="fancySelect"
                name="account"
                fancySelectOptions={{
                    isClearable: false,
                    fullScreenPlacement: 'right',
                    menuWidth: 300,
                    placeholder: 'Select an account...',
                }}
                value={condition.value}
                onChange={(option: StringOption) => {
                    onChange({ ...condition, value: option.value });
                }}
                options={accountOptions}
            />
        );
    }

    return null;
};

const ChangeBlock = ({
    changeConfig,
    change,
    onChange,
}: {
    changeConfig: ChangeConfig;
    change: RuleChangeWithEnabled;
    onChange: (condition: RuleChangeWithEnabled) => void;
}) => (
    <Block
        enabled={change.enabled}
        onToggle={(enabled) => {
            if (change.field === 'isHidden') {
                onChange({ ...change, enabled, value: enabled });
            } else {
                onChange({ ...change, enabled });
            }
        }}
        borderColor={change.enabled ? 'green.600' : 'gray.200'}
        label={changeConfig.label}
        switchColorScheme="green"
        cyLabel="change-block"
    >
        {change.field === 'merchantId' && (
            <MerchantSelect change={change} onChange={onChange} />
        )}
        {change.field === 'categoryId' && (
            <CategorySelect change={change} onChange={onChange} />
        )}
        {change.field === 'tagNames' && (
            <TagSelect change={change} onChange={onChange} />
        )}
    </Block>
);

const MerchantSelect = ({
    change,
    onChange,
}: {
    change: MerchantChange & Enabled;
    onChange: (condition: RuleChangeWithEnabled) => void;
}) => {
    const {
        merchantValue,
        handleChange,
        handleCreateOption,
        handleLoadOptions: loadMerchantOptions,
    } = useMerchantSelect({
        showLogo: true,
        merchantId: change.value,
    });

    return (
        <InputField
            type="fancySelect"
            name="change-merchantId"
            fancySelectOptions={{
                placeholder: 'Search merchants...',
                defaultOptions: true,
                loadOptions: loadMerchantOptions,
                onCreateOption: async (name: string) => {
                    const merchant = await handleCreateOption(name);

                    if (merchant) {
                        onChange({ ...change, value: merchant.id });
                    }
                },
                formatCreateLabel: (inputValue) =>
                    `Create merchant "${inputValue}"`,
                isClearable: false,
                fullScreenPlacement: 'left',
                menuWidth: 250,
            }}
            inputProps={{ size: 'sm' }}
            value={merchantValue}
            onChange={(option) => {
                handleChange(option);
                onChange({ ...change, value: option.value });
            }}
        />
    );
};

const CategorySelect = ({
    change,
    onChange,
}: {
    change: CategoryChange & Enabled;
    onChange: (condition: RuleChangeWithEnabled) => void;
}) => {
    const categoryOptions = useCategoryOptions();

    return (
        <InputField
            name="category"
            type="fancySelect"
            fancySelectOptions={{
                isClearable: false,
                fullScreenPlacement: 'left',
                menuWidth: 300,
                placeholder: 'Select a subcategory...',
            }}
            value={change.value}
            onChange={(option: StringOption) => {
                onChange({ ...change, value: option.value });
            }}
            options={categoryOptions}
            inputProps={{ size: 'sm' }}
        />
    );
};

const TagSelect = ({
    change,
    onChange,
}: {
    change: TagChange & Enabled;
    onChange: (condition: RuleChangeWithEnabled) => void;
}) => {
    const tagOptions = useTagOptions();

    return (
        <InputField
            name="tags"
            type="fancySelect"
            fancySelectOptions={{
                controlShouldRenderValue: true,
                isMulti: true,
                isClearable: true,
                fullScreenPlacement: 'left',
                menuWidth: 300,
                placeholder: 'Select tags...',
            }}
            value={change.value}
            onChange={(options: StringOption[]) => {
                const tagNames = options.map((o) => o.value);
                onChange({ ...change, value: tagNames });
            }}
            options={tagOptions}
            inputProps={{ size: 'sm' }}
        />
    );
};

const Block = ({
    onToggle,
    enabled,
    borderColor,
    label,
    switchColorScheme,
    children,
    cyLabel,
}: {
    onToggle: (enabled: boolean) => void;
    enabled: boolean;
    borderColor: string;
    label: string;
    switchColorScheme: 'green' | 'blue';
    cyLabel: string;
    children: React.ReactNode[];
}) => (
    <Box
        borderRadius="md"
        border="1px solid"
        borderColor={borderColor}
        px={3}
        py={4}
        transition="all 250ms ease-in-out"
        data-cy={cyLabel}
    >
        <FlexRow justify="space-between" align="center">
            <Text fontWeight="semibold">{label}</Text>
            <YesNoSelect
                value={enabled}
                onChange={onToggle}
                colorScheme={switchColorScheme}
                variant="flushed"
                size="sm"
            />
        </FlexRow>

        <Collapse in={enabled} animateOpacity>
            {children.some((c) => !!c) && (
                <FlexCol justify="space-between" align="center" mt={3} gap={2}>
                    {children}
                </FlexCol>
            )}
        </Collapse>
    </Box>
);
